import { z } from "zod";

/**
 * Coca AI — помощник владельца на Claude: вопросы о продажах, меню, складе и персонале своего заведения.
 * История диалога хранится в браузере и приходит целиком (сервер ничего не запоминает).
 */
export const AI_LANGS = ["ru", "ky", "uz", "en"] as const;

export const aiChatSchema = z
  .strictObject({
    messages: z
      .array(z.strictObject({ role: z.enum(["user", "assistant"]), content: z.string().trim().min(1).max(4000, { error: "Максимум 4000 символов" }) }))
      .min(1)
      .max(40),
    lang: z.enum(AI_LANGS).default("ru"),
  })
  .refine((b) => b.messages.at(-1)?.role === "user", { error: "Последнее сообщение — вопрос пользователя", path: ["messages"] });
export type AiChatInput = z.input<typeof aiChatSchema>;

/** Ответ помощника: текст (markdown) и какие данные он смотрел. */
export type AiChatReply = { text: string; tools: string[]; usage?: AiUsage };

/** Использование квоты `quota.ai_requests` за календарный месяц компании; `limit` null — без ограничений. */
export type AiUsage = { month: string; used: number; limit: number | null; remaining: number | null };
export type AiStatus = { enabled: boolean; usage?: AiUsage };

/**
 * Подсказки Coca AI на страницах: короткий совет по данным раздела за период.
 * Тема определяет отчёт, право и функцию тарифа (сервер `AiService.hint`). Готовая подсказка кэшируется — повторный показ квоту не тратит.
 */
export const AI_HINT_TOPICS = ["sales", "menu", "writeoffs", "products", "waiters", "finance", "stock"] as const;
export type AiHintTopic = (typeof AI_HINT_TOPICS)[number];

export const aiHintSchema = z.strictObject({
  topic: z.enum(AI_HINT_TOPICS),
  lang: z.enum(AI_LANGS).default("ru"),
  /** Период отчёта (у «stock» — не нужен) */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** true — пересчитать заново (тратит запрос), иначе — из кэша, если есть */
  refresh: z.boolean().default(false),
  /** true — только кэш: нет готовой подсказки → `text: null`, запрос не тратится */
  cachedOnly: z.boolean().default(false),
});
export type AiHintInput = z.input<typeof aiHintSchema>;
/** `text` null — подсказки в кэше нет (при `cachedOnly`). */
export type AiHintReply = { text: string | null; cached: boolean; createdAt: string | null; usage: AiUsage };
